import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { Phone, MapPin, Calendar } from "lucide-react";
import { MajolicaBand } from "@/components/MajolicaBand";

export const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer
      data-testid="site-footer"
      className="text-foreground border-t border-border"
      style={{ backgroundColor: "#C2DDED" }}
    >
      {/* Majolica tile band at the very top of the footer */}
      <MajolicaBand tiles={20} height={56} className="border-b border-[#1F5C8A]/15" />

      <div className="container-roze py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="font-serif text-4xl md:text-5xl leading-none mb-4">
            Roze
          </div>
          <p className="text-foreground/70 max-w-md leading-relaxed">
            {t("footer.tagline")}
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="overline mb-5">{t("footer.links_title")}</div>
          <ul className="space-y-2 text-sm">
            <li>
              <Link to="/" data-testid="footer-link-home" className="text-foreground/75 hover:text-foreground transition-colors">
                {t("nav.home")}
              </Link>
            </li>
            <li>
              <Link to="/about" data-testid="footer-link-about" className="text-foreground/75 hover:text-foreground transition-colors">
                {t("nav.about")}
              </Link>
            </li>
            <li>
              <Link to="/menu" data-testid="footer-link-menu" className="text-foreground/75 hover:text-foreground transition-colors">
                {t("nav.menu")}
              </Link>
            </li>
            <li>
              <Link to="/wine-list" data-testid="footer-link-wine" className="text-foreground/75 hover:text-foreground transition-colors">
                {t("nav.wine")}
              </Link>
            </li>
            <li>
              <Link to="/gallery" data-testid="footer-link-gallery" className="text-foreground/75 hover:text-foreground transition-colors">
                {t("nav.gallery")}
              </Link>
            </li>
            <li>
              <Link to="/events" data-testid="footer-link-events" className="text-foreground/75 hover:text-foreground transition-colors">
                {t("nav.events")}
              </Link>
            </li>
            <li>
              <Link to="/contact" data-testid="footer-link-contact" className="text-foreground/75 hover:text-foreground transition-colors">
                {t("nav.contact")}
              </Link>
            </li>
            <li>
              <Link to="/reserve" data-testid="footer-link-reserve" className="text-foreground hover:text-primary transition-colors">
                {t("nav.reserve")}
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="overline mb-5">{t("footer.contact_title")}</div>
          <ul className="space-y-3 text-sm text-foreground/80">
            <li className="flex items-start gap-3">
              <MapPin className="h-4 w-4 mt-0.5 text-primary" strokeWidth={1.5} />
              <span>{t("footer.address")}</span>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="h-4 w-4 mt-0.5 text-primary" strokeWidth={1.5} />
              <a href="tel:+37126111722" data-testid="footer-phone" className="hover:text-foreground">
                +371 26 111 722
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Calendar className="h-4 w-4 mt-0.5 text-primary" strokeWidth={1.5} />
              <span>{t("footer.season")}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-border/70">
        <div className="container-roze py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs uppercase tracking-[0.2em] text-foreground/50">
          <span>© {new Date().getFullYear()} Restorāns Roze · {t("footer.rights")}</span>
          <span className="font-serif italic text-base normal-case tracking-normal text-foreground/60">
            Maijs · September · with care
          </span>
        </div>
      </div>
    </footer>
  );
};
